/**
 * ENCAMINHAMENTO DE ÂMBITO (módulo puro — sem IA, sem Maps, sem créditos).
 *
 * Antes de perguntar "para que cidade queres viajar?", o pedido tem de ser
 * classificado: há pedidos que NUNCA são de uma cidade — estradas e percursos
 * nomeados, países, regiões, viagens intercidades e rotas que obrigam a mudar
 * de base. Esses vão para o planeador Pro com o contrato completo.
 *
 * Regra dura: nada aqui é específico de Lisboa, Peniche, Algarve ou Nacional 2.
 * Todos os corredores entram por DADOS (`CURATED_CORRIDORS`) e o resto por
 * padrões genéricos e multilingues.
 */

import { classifyGeoScope, needsRegionalPlanner, type GeoScopeKind } from "./geo-scope";
import { findCorridorInText, type CuratedCorridor } from "./catalog/corridor-routes";
import { detectNamedRouteExperience } from "./experience-types";
import { extractDestinationFromPrompt } from "./destination-extraction";
import { detectMotorcycleMode, type TransportMode } from "./transport-modes";

export type RegionalReason =
  | "curated_corridor"
  | "named_route"
  | "named_road"
  | "country"
  | "region"
  | "multi_city";

export type RegionalRequest = {
  reason: RegionalReason;
  /** Nome apresentável do âmbito (corredor, país, região, lista de cidades). */
  name: string;
  scopeKind: Extract<GeoScopeKind, "region" | "country" | "multi_city">;
  countryCode?: string;
  corridorId?: string;
  templateId?: string;
  /** Extremos oficiais do corredor, quando existe. */
  routeCities?: string[];
  transport?: TransportMode;
  minDays?: number;
};

const fold = (value: string) =>
  (value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/gu, "").toLowerCase();

/**
 * Estradas e itinerários nomeados que ainda não estão curados. Reconhecer o
 * FORMATO evita perguntar uma cidade a quem pediu uma estrada.
 */
const NAMED_ROAD_PATTERNS: RegExp[] = [
  /\b(?:estrada|rota|ruta|route|road|strada|straße|strasse|carretera|autoestrada|highway|itinerario|itinerário)\s+(?:nacional\s+)?(?:n[ºo°]?\s*)?\d{1,4}\b/u,
  /\b(?:en|n|sr|us|sp|a|ss|d)\s?-?\s?\d{1,4}\b\s*(?:de\s+carro|de\s+mota|road|estrada)\b/u,
  /\b(?:caminho|camino|way|cammino|weg)\s+(?:de\s+)?santiago\b/u,
  /\b(?:volta|tour|circuito|circuit|loop|giro)\s+(?:a|ao|à|de|do|da|of|du|della)\s+\p{L}{3,}/u,
];

/**
 * Pedidos que exigem mudar de base ("de X a Y", "road trip", "vários dias
 * a atravessar").
 */
const INTERCITY_PATTERNS: RegExp[] = [
  /\b(?:de|from|da|von|desde)\s+\p{Lu}[\p{L}'’-]{2,}\s+(?:a|ate|até|para|to|a[ ]fino|fino a|bis|hasta|jusqu'a|jusqu'à)\s+\p{Lu}[\p{L}'’-]{2,}/u,
  /\broad\s?trip\b/u,
  /\b(?:atravessar|percorrer|cruzar|cross|traverse|durchqueren)\b/u,
];

function corridorMinDays(corridor: CuratedCorridor): number {
  const ordered = [...corridor.durationPresets].sort((a, b) => a.days - b.days);
  // O ritmo intermédio é o default honesto para o handoff. O percurso rápido
  // continua disponível, mas não deve apagar etapas obrigatórias à partida.
  return ordered[Math.floor((ordered.length - 1) / 2)]?.days ?? ordered[0]?.days ?? 2;
}

/**
 * Classifica o pedido. Devolve `null` quando é mesmo um pedido de cidade
 * (o fluxo Premium/Free normal).
 */
export function detectRegionalRequest(
  text: string,
  language = "pt",
): RegionalRequest | null {
  const raw = (text ?? "").replace(/\s+/gu, " ").trim();
  if (!raw) return null;
  const folded = fold(raw);

  // 1. Corredor curado (dados verificados: geometria, extremos, durações).
  const corridor = findCorridorInText(raw);
  if (corridor) {
    const first = corridor.waypoints[0]?.name;
    const last = corridor.waypoints[corridor.waypoints.length - 1]?.name;
    return {
      reason: "curated_corridor",
      name: corridor.name,
      scopeKind: "multi_city",
      countryCode: corridor.countryCodes[0],
      corridorId: corridor.id,
      templateId: corridor.templateId,
      routeCities: [first, last].filter(Boolean) as string[],
      transport: detectMotorcycleMode(raw) ? "motorcycle" : "car",
      minDays: corridorMinDays(corridor),
    };
  }

  // 2. Experiência nomeada multi-localidade (comboios panorâmicos, trilhos).
  const named = detectNamedRouteExperience(raw);
  if (named?.multiLocality) {
    const anchors = (named.template.anchors ?? []).filter(Boolean);
    return {
      reason: "named_route",
      name: named.template.name,
      scopeKind: "multi_city",
      countryCode: named.template.countryCodes?.[0],
      templateId: named.template.id,
      routeCities: [anchors[0], anchors[anchors.length - 1]].filter(Boolean) as string[],
      transport: named.suggestedTransport,
      minDays: named.recommendedDays,
    };
  }

  // 3. Estrada/itinerário nomeado ainda não curado.
  if (NAMED_ROAD_PATTERNS.some((re) => re.test(folded))) {
    return {
      reason: "named_road",
      name: raw,
      scopeKind: "multi_city",
      transport: "car",
      minDays: 2,
    };
  }

  // 4. País / região / várias cidades a partir do destino extraído.
  const candidate = extractDestinationFromPrompt(raw).city;
  if (candidate) {
    const scope = classifyGeoScope(candidate, { language });
    if (needsRegionalPlanner(scope)) {
      return {
        reason: scope.kind === "country" ? "country" : scope.kind === "region" ? "region" : "multi_city",
        name: scope.name,
        scopeKind: scope.kind as RegionalRequest["scopeKind"],
        countryCode: scope.countryCode,
      };
    }
  }

  // 5. Pedidos intercidades explícitos.
  if (INTERCITY_PATTERNS.some((re) => re.test(raw) || re.test(folded))) {
    return { reason: "multi_city", name: candidate ?? raw, scopeKind: "multi_city", minDays: 2 };
  }

  return null;
}
